import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { submitForm } from '../../services';
import Seo from '../Seo';

const TSHIRT_SIZES = ['YS', 'YM', 'YL', 'S', 'M', 'L', 'XL', 'XXL'];

interface FormState {
  participantName: string;
  dateOfBirth: string;
  age: string;
  grade: string;
  gender: string;
  tshirtSize: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  parentGuardianName: string;
  phoneNumber: string;
  email: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  relationship: string;
  foodAllergies: string;
  hasMedicalCondition: string;
  medicalExplanation: string;
  photoRelease: string;
  parentSignature: string;
  signatureDate: string;
}

const initialState: FormState = {
  participantName: '',
  dateOfBirth: '',
  age: '',
  grade: '',
  gender: '',
  tshirtSize: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  parentGuardianName: '',
  phoneNumber: '',
  email: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  relationship: '',
  foodAllergies: '',
  hasMedicalCondition: '',
  medicalExplanation: '',
  photoRelease: '',
  parentSignature: '',
  signatureDate: '',
};

const VBS2026 = () => {
  const [form, setForm] = useState<FormState>(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange =
    (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !form.participantName ||
      !form.parentGuardianName ||
      !form.phoneNumber
    ) {
      setError('Please fill in all required fields.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await submitForm('youth-vbs-2026', {
        ...form,
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Alert severity="success" sx={{ mb: 3, fontSize: '1.1rem' }}>
          <strong>Registration submitted!</strong>
          <br />
          Thank you for registering for VBS 2026. We can{`'`}t wait to see you!
        </Alert>
        <Typography variant="body1" color="text.secondary">
          June 22–26, 2026 &bull; 5:30 PM – 8:00 PM
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 3 }}
          onClick={() => {
            setForm(initialState);
            setSubmitted(false);
          }}
        >
          Register Another Participant
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 4 }}>
      <Seo
        title="Youth VBS 2026"
        description="Register for Vacation Bible School 2026 at New Mt. Zion Missionary Baptist Church in Lakeland, FL."
        path="/youth-vbs-2026"
      />
      <Container maxWidth="md">
        {/* Header Banner */}
        <Box
          sx={{
            border: '4px solid #2e7d32',
            borderRadius: '8px 8px 0 0',
            overflow: 'hidden',
            lineHeight: 0,
          }}
        >
          <Box
            component="img"
            src={require('../../images/leaders/vbs_header.png')}
            alt="New Mt. Zion VBS 2026 - The Jesus Connection"
            sx={{
              width: '100%',
              display: 'block',
              objectFit: 'cover',
              objectPosition: 'top',
              maxHeight: '320px',
            }}
          />
        </Box>

        {/* Form */}
        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={3}
          sx={{
            border: '4px solid #2e7d32',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            mt: 0,
            p: { xs: 2, sm: 4 },
          }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{ fontWeight: 900, mb: 0.5 }}
          >
            <Box component="span" sx={{ color: '#d32f2f' }}>
              VBS{' '}
            </Box>
            <Box component="span" sx={{ color: '#2e7d32' }}>
              REGISTRATION{' '}
            </Box>
            <Box component="span" sx={{ color: '#e65100' }}>
              FORM
            </Box>
          </Typography>
          <Typography
            align="center"
            variant="body2"
            sx={{ fontStyle: 'italic', mb: 1.5 }}
          >
            Please fill out completely. One form per participant.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mb: 3,
            }}
          >
            <Box
              sx={{
                bgcolor: '#1565c0',
                color: 'white',
                borderRadius: 1,
                px: 2.5,
                py: 0.75,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, display: 'block' }}
              >
                DATES
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                JUNE 22–26, 2026
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: '#2e7d32',
                color: 'white',
                borderRadius: 1,
                px: 2.5,
                py: 0.75,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, display: 'block' }}
              >
                TIME
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                5:30PM – 8:00PM
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Participant Info */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Participant's Full Name"
                value={form.participantName}
                onChange={handleChange('participantName')}
                variant="standard"
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={form.dateOfBirth}
                onChange={handleChange('dateOfBirth')}
                variant="standard"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Age"
                value={form.age}
                onChange={handleChange('age')}
                variant="standard"
                inputProps={{ maxLength: 3 }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                label="Grade (2026–2027)"
                value={form.grade}
                onChange={handleChange('grade')}
                variant="standard"
              />
            </Grid>

            {/* Gender */}
            <Grid item xs={12} sm={5}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>
                  Gender
                </FormLabel>
                <RadioGroup
                  row
                  value={form.gender}
                  onChange={handleChange('gender')}
                >
                  <FormControlLabel
                    value="male"
                    control={<Radio size="small" />}
                    label="Male"
                  />
                  <FormControlLabel
                    value="female"
                    control={<Radio size="small" />}
                    label="Female"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* T-Shirt Size */}
            <Grid item xs={12} sm={7}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: '0.85rem' }}>
                  T-Shirt Size (Youth/Adult)
                </FormLabel>
                <FormGroup row>
                  {TSHIRT_SIZES.map((size) => (
                    <FormControlLabel
                      key={size}
                      label={size}
                      control={
                        <Checkbox
                          size="small"
                          checked={form.tshirtSize === size}
                          onChange={() =>
                            setForm((prev) => ({
                              ...prev,
                              tshirtSize: prev.tshirtSize === size ? '' : size,
                            }))
                          }
                        />
                      }
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={form.address}
                onChange={handleChange('address')}
                variant="standard"
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="City"
                value={form.city}
                onChange={handleChange('city')}
                variant="standard"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="State"
                value={form.state}
                onChange={handleChange('state')}
                variant="standard"
                inputProps={{ maxLength: 2 }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                label="Zip Code"
                value={form.zipCode}
                onChange={handleChange('zipCode')}
                variant="standard"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Parent/Guardian Name"
                value={form.parentGuardianName}
                onChange={handleChange('parentGuardianName')}
                variant="standard"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                value={form.phoneNumber}
                onChange={handleChange('phoneNumber')}
                variant="standard"
                type="tel"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={form.email}
                onChange={handleChange('email')}
                variant="standard"
                type="email"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Emergency Contact Name (If Different)"
                value={form.emergencyContactName}
                onChange={handleChange('emergencyContactName')}
                variant="standard"
              />
            </Grid>

            <Grid item xs={12} sm={7}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                value={form.emergencyContactPhone}
                onChange={handleChange('emergencyContactPhone')}
                variant="standard"
                type="tel"
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Relationship"
                value={form.relationship}
                onChange={handleChange('relationship')}
                variant="standard"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Food Allergies or Dietary Restrictions"
                value={form.foodAllergies}
                onChange={handleChange('foodAllergies')}
                variant="standard"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Medical & Photo Release */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{ p: 2, borderColor: '#2e7d32', height: '100%' }}
              >
                <Typography
                  variant="subtitle2"
                  align="center"
                  sx={{ color: '#2e7d32', fontWeight: 700, mb: 1 }}
                >
                  MEDICAL INFORMATION
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Does the participant have any medical conditions, allergies,
                  or special needs we should be aware of?
                </Typography>
                <RadioGroup
                  row
                  value={form.hasMedicalCondition}
                  onChange={handleChange('hasMedicalCondition')}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio size="small" />}
                    label="YES"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio size="small" />}
                    label="NO"
                  />
                </RadioGroup>
                {form.hasMedicalCondition === 'yes' && (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="If yes, please explain"
                    value={form.medicalExplanation}
                    onChange={handleChange('medicalExplanation')}
                    variant="standard"
                    sx={{ mt: 1 }}
                  />
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{ p: 2, borderColor: '#1565c0', height: '100%' }}
              >
                <Typography
                  variant="subtitle2"
                  align="center"
                  sx={{ color: '#1565c0', fontWeight: 700, mb: 1 }}
                >
                  PHOTO/VIDEO RELEASE
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  I give permission for photos and/or video of my child to be
                  taken during VBS and used for church promotional purposes.
                </Typography>
                <RadioGroup
                  row
                  value={form.photoRelease}
                  onChange={handleChange('photoRelease')}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio size="small" />}
                    label="YES"
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio size="small" />}
                    label="NO"
                  />
                </RadioGroup>
                <TextField
                  fullWidth
                  label="Parent/Guardian Signature"
                  value={form.parentSignature}
                  onChange={handleChange('parentSignature')}
                  variant="standard"
                  sx={{ mt: 1 }}
                />
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={form.signatureDate}
                  onChange={handleChange('signatureDate')}
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 1 }}
                />
              </Paper>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Scripture */}
          <Typography
            align="center"
            variant="body2"
            sx={{ fontStyle: 'italic', mb: 3, color: '#444' }}
          >
            <em>
              For God so loved the world that He gave His one and only Son, that
              whoever believes in Him shall not perish but have eternal life.
            </em>
            <br />
            <strong>John 3:16 (NIV)</strong>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{
                bgcolor: '#2e7d32',
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                '&:hover': { bgcolor: '#1b5e20' },
              }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Submit Registration'
              )}
            </Button>
          </Box>

          {/* Footer icons row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 2, sm: 4 },
              mt: 4,
              flexWrap: 'wrap',
            }}
          >
            {[
              { emoji: '🎨', label: 'AWESOME CRAFTS' },
              { emoji: '🎵', label: 'PRAISE & WORSHIP' },
              { emoji: '✝️', label: 'POWERFUL BIBLE LESSONS' },
              { emoji: '🏃', label: 'FUN GAMES & ACTIVITIES' },
              { emoji: '🍽️', label: 'DINNER SERVED DAILY' },
            ].map((item) => (
              <Box key={item.label} sx={{ textAlign: 'center', minWidth: 70 }}>
                <Typography variant="h5">{item.emoji}</Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: '#444', lineHeight: 1.2 }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              bgcolor: '#1a3a1a',
              borderRadius: 1,
              mt: 3,
              py: 1.5,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: 'white', fontWeight: 700 }}
            >
              THANK YOU! WE CAN{`'`}T WAIT TO SEE YOU AT VBS!
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default VBS2026;
